import React, { useState, useEffect, useRef, useCallback } from "react";
import { notificationsAPI } from "../api/client";

let notificationAudioCtx = null;

const getNotificationAudioContext = () => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!notificationAudioCtx || notificationAudioCtx.state === "closed") {
    notificationAudioCtx = new Ctx();
  }
  return notificationAudioCtx;
};

// Soft marimba arpeggio (C6 - E6 - G6) for new notifications. Synthesised
// with Web Audio so there is no asset to load; the wooden marimba character
// comes from a strong 4th harmonic and a short, fast decay.
const playNotificationSound = () => {
  try {
    const ctx = getNotificationAudioContext();
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});

    const start = ctx.currentTime + 0.02;
    const master = ctx.createGain();
    master.gain.value = 0.14; // softer - not intrusive in an office
    master.connect(ctx.destination);

    const notes = [
      { freq: 1046.5, at: 0, level: 1 },
      { freq: 1318.51, at: 0.09, level: 0.85 },
      { freq: 1567.98, at: 0.18, level: 0.75 },
    ];
    const partials = [
      [1, 1],
      [4, 0.18],
      [10, 0.05],
    ];
    const decay = 0.6;

    notes.forEach(({ freq, at, level }) => {
      const t0 = start + at;
      partials.forEach(([mult, partialLevel]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq * mult, t0);
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.linearRampToValueAtTime(level * partialLevel, t0 + 0.004);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + decay);
        osc.connect(gain);
        gain.connect(master);
        osc.start(t0);
        osc.stop(t0 + decay + 0.02);
      });
    });
  } catch {
    // audio not supported / blocked
  }
};

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const pollingRef = useRef(null);
  const prevUnreadRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const [countRes, notifRes] = await Promise.all([
        notificationsAPI.getUnreadCount(),
        notificationsAPI.getAll(),
      ]);
      const newCount = countRes.data.count;
      if (prevUnreadRef.current !== null && newCount > prevUnreadRef.current) {
        playNotificationSound();
      }
      prevUnreadRef.current = newCount;
      setUnreadCount(newCount);
      setNotifications(notifRes.data);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    pollingRef.current = setInterval(fetchNotifications, 60000);
    return () => clearInterval(pollingRef.current);
  }, [fetchNotifications]);

  // Browsers keep an AudioContext suspended until the user interacts with the
  // page. Resume it on the first click/keypress so the notification sound is
  // not silently swallowed when the bell first receives something.
  useEffect(() => {
    const unlock = () => {
      const ctx = getNotificationAudioContext();
      if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setShowDropdown(false);
    };
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showDropdown]);

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // silent
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch {
      // silent
    }
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  };

  const hasUnread = notifications.some((n) => !n.is_read);

  return (
    <div className="notification-bell-wrap" ref={dropdownRef}>
      <button
        type="button"
        className="notification-bell"
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
        aria-expanded={showDropdown}
        aria-haspopup="true"
        aria-controls="notification-dropdown"
        onClick={() => setShowDropdown((prev) => !prev)}
      >
        🔔
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div
          id="notification-dropdown"
          className="notification-dropdown"
          role="dialog"
          aria-label="Notifications"
        >
          <div className="notification-dropdown-header">
            <span className="notification-dropdown-title">Notifications</span>
            {hasUnread && (
              <button
                type="button"
                className="notification-mark-all-btn"
                onClick={handleMarkAllRead}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">No notifications yet</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`notification-item ${n.is_read ? "read" : "unread"}`}
                >
                  <div className="notification-item-content">
                    <div className="notification-item-title">{n.title}</div>
                    <div className="notification-item-message">{n.message}</div>
                    <div className="notification-item-time">
                      {formatTime(n.created_at)}
                    </div>
                  </div>
                  {!n.is_read && (
                    <button
                      type="button"
                      className="notification-mark-read-btn"
                      onClick={() => handleMarkRead(n.id)}
                      aria-label="Mark as read"
                    >
                      ✓
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
